import AccountService from "@/services/common/auth/AccountService";
import ToolBarService from "@/services/common/ToolBarService";
import UsersService from "@/services/common/UsersService";
import MonitoringElementService from "@/services/MonitoringElementsService";
import TokenService from "@/services/common/auth/TokenService";
import store from "@/stores/store";
import DateTimeHandler from "@/services/common/date-time/DateTimeHandlerService";
import CompaniesService from "@/services/common/CompaniesService";
import router from "@/routers/router";
import i18n from "@/i18n";
import { SET } from "@/stores/CommumActionTypes";

/**
 * @summary Regras do Token para o guard das rotas.
 * @returns {Boolean} retorna true se as regras foram cumpridas e false se não foram.
 */
const TokenGuardRule = async function()
{
	try
	{
		// Se estiver expirado o token e se o usuário estiver autenticado.
		if (IsExpired() && IsAuthenticated())
		{
			// Pega o token de acesso.
			const acessToken = await AccountService.GetRefresheToken();
			return !!acessToken.token;
		}
		return true;
	}
	catch (error)
	{
		throw error;
	}
};

/**
 * @summary Regras de preferências do usuário para o guard das rotas.
 * @returns {Boolean - Object} retorna as preferências se as regras foram cumpridas e false se não foram para sair
 * da rota.
 */
const UserPreferencesGuardRouteRule = async function()
{
	try
	{
		// Está autenticado e as preferências do usuário carregada.
		if (IsAuthenticated() && IsUserPreferencesLoaded())
		{
			const userPreferences = store.state.UserModule.UserPreferencesModule.userPreferences;
			// Retorna as preferências do usuário salvo na store.
			return userPreferences;
		}
		// Autenticado e preferências do usuário não carregadas.
		else if (IsAuthenticated() && !IsUserPreferencesLoaded())
		{
			// Pega as preferências do usuário
			const userId = store.state.UserModule.user.id;
			const userPreferences = await UsersService.GetUserPreferences(userId);

			// Retorna os preferências do usuário.
			return userPreferences;
		}
		return false;
	}
	catch (error)
	{
		throw error;
	}
};

/**
 * @summary Regras do usuário para o guard das rotas.
 * @returns {Boolean - Object} retorna usuário se as regras foram cumpridas e false se não foram para sair da rota.
 */
const UserGuardRouteRule = async function()
{
	try
	{
		// Está autenticado e o usuário carregado.
		if (IsAuthenticated() && IsUserLoaded())
		{
			const user = store.state.UserModule.user;

			// Retorna o usuário salvo na store.
			return user;
		}

		// Autenticado e usuário não carregado.
		else if (IsAuthenticated() && !IsUserLoaded())
		{
			// Chama o usuário
			const user = await UsersService.GetMe();

			// Retorna o usuário
			return user;
		}
		return false;
	}
	catch (error)
	{
		throw error;
	}
};

/**
 * @summary Regras da company para o guard das rotas.
 * @param {Number} companyId - Id da empresa do usuário.
 * @returns {Boolean} retorna true se a empresa existir na memória e false se não existir para sair da rota.
 */
const CompanyGuardRouteRule = async function(companyId = 0)
{
	 try
	 {
		if (process.env.VUE_APP_IS_SAE === "true")
		{
			return HandleHydroNavigationGuard(companyId);
		}
		else
		{
			return HandleWindNavigationGuard(companyId);
		}
	 }
	 catch (error)
	 {
		 throw error;
	 }
};

const HandleHydroNavigationGuard = async function(companyId)
{
	// Está autenticado e o company carregado.
	if (IsAuthenticated() && IsCompanyLoaded()) return true;

	// Autenticado e company não carregado.
	else if (IsAuthenticated() && !IsCompanyLoaded())
	{
		// Chama a company
		const company = await CompaniesService.GetCompany(companyId);
		return !!company && !!company.name;
	}
	return false;
};

const HandleWindNavigationGuard = async function(companyId)
{
	// Está autenticado e o company carregado.
	if (IsAuthenticated() && IsCompanyLoaded() && IsWindEnvironmentLoaded()) return true;

	// Autenticado e company não carregado.
	else if (IsAuthenticated() && (!IsCompanyLoaded() || !IsWindEnvironmentLoaded()))
	{
		// Chama a company.
		const company = await CompaniesService.GetCompany(companyId);

		// Carrega a estrutura de monitoramento da empresa.
		const monitoringElementTree = await CompaniesService.GetWindMonitoringElementTree(companyId);
		store.dispatch(`WindAssetModule/${SET}`, monitoringElementTree);

		return !!company && !!company.name;
	}
	return false;
};

/**
 * @summary Regras do menu lateral e monitoringelements ao qual o usuário pode acessar das rotas.
 * @param {Number} companyId - Id da empresa do usuário.
 * @returns {Boolean} Retorna true se cumprir as regras e false se não cumprir.
 */
export const MenuGuardRouteRule = async function(companyId = 0)
{
	try
	{
		// Se está autenticado e o menu carregado.
		if (IsAuthenticated() && IsMenuLoaded())
		{
			return true;
		}
		// Se está autenticado e o menu não está carregado.
		else if (IsAuthenticated() && !IsMenuLoaded())
		{
			const menu = await ToolBarService.GetToolBarTreeMenu(companyId);

			// Retorna true se o menu existir.
			return !!menu && menu.length > 0;
		}
		return false;
	}
	catch (error)
	{
		throw error;
	}
};

/**
 * @summary Wrapper das funções que verificam as configurações minimas para a troca de rotas.
 * @returns {Boolean} retorna true se as configurações inicias para a aplicação estão carregadas.
 */
export const DefaultSettingsLoaded = async function()
{
	try
	{
		let canRouteEnter = false;

		canRouteEnter = await TokenGuardRule();
		if (!canRouteEnter) return canRouteEnter;

		const user = await UserGuardRouteRule();
		canRouteEnter = !!user.name;
		if (!canRouteEnter) return canRouteEnter;

		canRouteEnter = await UserPreferencesGuardRouteRule();
		if (!canRouteEnter) return canRouteEnter;

		canRouteEnter = await CompanyGuardRouteRule(user.companiesId);
		if (!canRouteEnter) return canRouteEnter;

		canRouteEnter = await MenuGuardRouteRule(user.companiesId);
		return canRouteEnter;
	}
	catch (error)
	{
		await AccountService.Logout();
	}
};

/**
 * @summary Verifica se o MonitoringElement faz parte do menu lateral.
 * @param {Number} monitoringElmentId - Id do monitoringElement que o usuário quer acessar.
 * @returns {Boolean} Retorna true se o Id existe na árvore de monitoringElements do menu lateral.
 */
export const CanAcessMonitoringElement = function(monitoringElmentId)
{
	const { menu } = store.state.ToolbarTreeMenuModule;
	return MonitoringElementService.IsThereAMonitoringElementInTreeMenu(monitoringElmentId, menu);
};

/**
 * @summary Verifica se a autenticação do usuário foi comitada na store.
 * @returns {boolean} - Usuário autenticado.
 */
export const IsAuthenticated = () => store.getters["AccountModule/isAuthenticated"];

/**
 * @summary Verifica se o usuário foi comitado na store.
 * @returns {boolean} - Usuário foi carregado.
 */
export const IsUserLoaded = () => { return !!store.state.UserModule.user.name; };

/**
 * @summary Verifica se as preferências do usuário foram comitadas na store.
 * @returns {boolean} - Preferências do usuário foram carregadas.
 */
export const IsUserPreferencesLoaded = () =>
{
	return !!store.state.UserModule.UserPreferencesModule.userPreferences.theme;
};

/**
 * @summary Verifica se o usuário é CompanyAdministrator na store.
 * @returns {boolean} - Usuário é Company Administrator.
 */
export const IsCompanyAdmin = () => store.getters["UserModule/isCompanyAdministrator"];

/**
 * @summary Verifica se o usuário é Company Administrator na store.
 * @returns {boolean} - Usuário é Global Adminitrator.
 */
export const IsGlobalAdmin = () => store.getters["UserModule/isGlobalAdministrator"];

/**
 * @summary Verifica se o menu foi comitado na store.
 * @returns {boolean} - Menu foi carregado.
 */
export const IsMenuLoaded = () =>
{
	return !!store.state.ToolbarTreeMenuModule.menu && store.state.ToolbarTreeMenuModule.menu.length > 0;
};

/**
 * @summary Verifica se a empresa foi comitado na store.
 * @returns {boolean} - Empresa foi carregada.
 */
export const IsCompanyLoaded = () =>
{
	const companyModule = store.state.CompanyModule;
	return !!companyModule.company && !!companyModule.company.name;
};

/**
 * @summary Verifica se a árvore monitorada da empresa foi comitado na store.
 * @returns {boolean} - Árvore de monitoramento foi carregada.
 */
export const IsWindEnvironmentLoaded = () =>
{
	const windAssetsModule = store.state.WindAssetModule;
	return !!windAssetsModule.windMonitoringElementsTree && windAssetsModule.windMonitoringElementsTree.length > 0;
};

/**
 * @summary Verifica se o JWT token expirou.
 * @returns {boolean} - Se o token expirou.
 */
const IsExpired = function()
{
	const expirationTokenDate = TokenService.GetJWTExpiredAtCookie();

	const difference = Math.ceil(DateTimeHandler.TimeDifferenceInMinutesFromNow(expirationTokenDate));

	return difference >= 0;
};

/**
 * Define o idioma da aplicação e atualiza as configurações correspondentes.
 * @param {string} locale - O código do idioma a ser definido.
 */
export const SetLocale = function(locale)
{
	localStorage.setItem("locale", locale);
	i18n.locale = locale;

	const currentRoute = router.currentRoute;
	currentRoute.params.lang = i18n.locale;
	router.push({
		name:   currentRoute.name,
		params: currentRoute.params,
		query:  currentRoute.query,
	});
};

/**
 * Verifica se o idioma é suportado e redireciona para o idioma adequado, se necessário.
 * @param {Object} to - O objeto que representa a rota de destino.
 * @param {Object} from - O objeto que representa a rota de origem.
 * @param {Function} next - Função para prosseguir com a navegação.
 */
export const OnLanguageSupportCheck = function(to, from, next)
{
	const { lang } = to.params;

	const supportedLanguages = process.env.VUE_APP_I18N_SUPPORTED_LANGUAGE.split(",");

	if (!supportedLanguages.includes(lang))
	{
		const langLocalStorage = localStorage.getItem("locale");

		if (supportedLanguages.includes(langLocalStorage))
		{
			return next(langLocalStorage);
		}

		return next("pt-BR");
	}

	if (i18n.locale !== lang)
	{
		i18n.locale = lang;
	}

	localStorage.setItem("locale", lang);
	return next();
};
